import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Pill, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchMedication } from "@/lib/api";
import BlogBody from "@/components/BlogBody";
import { MED } from "@/constants/testIds";

export default function MedicationDetail() {
  const { id } = useParams();
  const [med, setMed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    fetchMedication(id)
      .then((data) => alive && setMed(data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20 flex justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </main>
    );
  }

  if (error || !med) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <Link
          to="/know-your-medications"
          data-testid={MED.backLink}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft size={16} /> All medications
        </Link>
        <h1 className="font-serif-display text-4xl text-slate-900 mt-6">
          Medication not found
        </h1>
      </main>
    );
  }

  return (
    <main data-testid={MED.detail} className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
      <Link
        to="/know-your-medications"
        data-testid={MED.backLink}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ChevronLeft size={16} /> All medications
      </Link>

      <div className="mt-7 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--primary))]/20 grid place-items-center text-slate-900">
          <Pill size={22} />
        </div>
        <div>
          {med.drug_class ? (
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
              {med.drug_class}
            </div>
          ) : null}
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight">
            {med.name}
          </h1>
        </div>
      </div>

      {med.uses ? (
        <p className="text-slate-600 mt-7 text-lg leading-relaxed">{med.uses}</p>
      ) : null}

      <article className="mt-8">
        <BlogBody html={med.body} />
      </article>

      <div className="mt-14 rounded-2xl bg-slate-900 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="font-serif-display text-2xl md:text-3xl">
            Discuss your prescription
          </div>
          <div className="text-slate-300 text-sm mt-2">
            Book a 20-minute consultation to review every medication and dose.
          </div>
        </div>
        <Link to="/#book">
          <Button className="rounded-full h-11 px-7 bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90">
            Book Appointment
          </Button>
        </Link>
      </div>

      <p className="text-xs text-slate-500 mt-8">
        Educational information only. Do not start, stop, or change any medication without your doctor.
      </p>
    </main>
  );
}
